(() => {
  if (window.hrHomepageBoot) {
    window.hrHomepageBoot();
    return;
  }
  const instances = new WeakMap();
  const initialise = (root) => {
    if (!root || instances.has(root)) return;
    const controller = new AbortController();
    const { signal } = controller;
    const timers = new Set();
    const schedule = (callback, delay) => {
      const id = window.setTimeout(() => {
        timers.delete(id);
        if (!signal.aborted) callback();
      }, delay);
      timers.add(id);
    };
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const desktop = window.matchMedia('(min-width: 900px)');
    const videos = [...root.querySelectorAll('[data-hr-video]')].filter((video) => !video.closest('[data-hr-sequence]'));
    const visible = new Set();
    let observer;
    const appropriateDevice = (video) => {
      if (video.classList.contains('hr-hero__video--desktop')) return desktop.matches;
      if (video.classList.contains('hr-hero__video--mobile')) return !desktop.matches;
      return true;
    };
    const syncButton = (video) => {
      const button = video.closest('.hr-video-card, .hr-ugc-card')?.querySelector('[data-hr-video-toggle]');
      if (!button) return;
      const playing = !video.paused && !video.ended;
      const label = (button.getAttribute('aria-label') || '').replace(/^(Play|Pause) /, '');
      button.setAttribute('aria-label', `${playing ? 'Pause' : 'Play'} ${label}`);
      if (button.querySelector('span')) button.querySelector('span').textContent = playing ? 'Pause' : 'Play';
      else button.textContent = playing ? 'Ⅱ' : '▶';
    };
    const hydrateVideo = (video) => {
      if (!video.dataset.src || video.getAttribute('src')) return;
      video.src = video.dataset.src;
      video.removeAttribute('data-src');
      video.load();
    };
    const canPlay = (video) => visible.has(video) && appropriateDevice(video) && !document.hidden &&
      video.dataset.hrUserPaused !== 'true' && (!reducedMotion.matches || video.dataset.hrUserStarted === 'true');
    const reconcile = (video) => {
      if (!canPlay(video)) {
        video.pause();
        return;
      }
      hydrateVideo(video);
      if (video.paused) video.play().then(() => {
        if (signal.aborted || !canPlay(video)) video.pause();
      }).catch(() => syncButton(video));
    };
    const reconcileAll = () => videos.forEach(reconcile);
    videos.forEach((video) => {
      video.addEventListener('play', () => syncButton(video), { signal });
      video.addEventListener('pause', () => syncButton(video), { signal });
      video.addEventListener('ended', () => syncButton(video), { signal });
      syncButton(video);
    });
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        entries.forEach(({ target, isIntersecting, intersectionRatio }) => {
          if (isIntersecting && intersectionRatio > 0) visible.add(target);
          else visible.delete(target);
          reconcile(target);
        });
      }, { threshold: 0.01 });
      videos.forEach((video) => observer.observe(video));
    } else {
      videos.filter((video) => video.classList.contains('hr-hero__video')).forEach((video) => visible.add(video));
      reconcileAll();
    }
    document.addEventListener('visibilitychange', reconcileAll, { signal });
    reducedMotion.addEventListener('change', reconcileAll, { signal });
    desktop.addEventListener('change', reconcileAll, { signal });
    root.addEventListener('click', (event) => {
      const button = event.target.closest('[data-hr-video-toggle]');
      if (!button) return;
      const video = button.closest('.hr-video-card, .hr-ugc-card')?.querySelector('video');
      if (!video) return;
      if (video.paused) {
        video.dataset.hrUserPaused = 'false';
        video.dataset.hrUserStarted = 'true';
        visible.add(video);
      } else {
        video.dataset.hrUserPaused = 'true';
      }
      reconcile(video);
    }, { signal });
    root.querySelectorAll('[data-hr-quick-form]').forEach((form) => {
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const button = form.querySelector('button[type="submit"]');
        if (!button || button.disabled) return;
        const originalLabel = button.textContent;
        button.disabled = true;
        button.textContent = 'Adding…';
        try {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form)
          });
          if (!response.ok) throw new Error('Unable to add this product');
          const item = await response.json();
          button.textContent = 'Added';
          button.classList.add('is-added');
          document.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'product:added', item } }));
          schedule(() => {
            button.textContent = originalLabel;
            button.classList.remove('is-added');
            button.disabled = false;
          }, 1800);
        } catch (error) {
          button.textContent = 'Try again';
          button.disabled = false;
          schedule(() => { button.textContent = originalLabel; }, 1800);
        }
      }, { signal });
    });
    instances.set(root, () => {
      controller.abort();
      observer?.disconnect();
      videos.forEach((video) => video.pause());
      timers.forEach((id) => window.clearTimeout(id));
      instances.delete(root);
    });
  };
  const boot = () => document.querySelectorAll('[data-hr-home]').forEach(initialise);
  window.hrHomepageBoot = boot;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.matches('[data-hr-home]')) initialise(event.target);
    event.target.querySelectorAll('[data-hr-home]').forEach(initialise);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches('[data-hr-home]')) instances.get(event.target)?.();
    event.target.querySelectorAll('[data-hr-home]').forEach((root) => instances.get(root)?.());
  });
})();
